import {type FlowEntity, type FlowLink, type FlowSetup, type FlowValidator} from '../types'

export const removeInvalidRefs = (
  setups: FlowSetup[],
  entities: FlowEntity[],
  terminalNodeId: string
): FlowSetup[] => {
  const validIds = new Set([...entities.map((e) => e.id), terminalNodeId])
  return setups.map((setup) => ({
    ...setup,
    links: setup.links.filter(
      (link) => validIds.has(link.sourceNodeId) && validIds.has(link.targetNodeId)
    ),
    positions: setup.positions.filter((pos) => validIds.has(pos.nodeId))
  }))
}

interface DefaultValidatorOptions {
  terminalNodeId: string
  maxChainDepth: number
}

export const createDefaultValidator = ({
  terminalNodeId,
  maxChainDepth
}: DefaultValidatorOptions): FlowValidator => {
  const isTerminal = (id: string) => id === terminalNodeId

  const isConnectionValid = (
    sourceId: string,
    targetId: string,
    existingLinks: FlowLink[]
  ): {valid: true} | {valid: false; reason: 'duplicate' | 'cycle' | 'maxDepth'} => {
    if (existingLinks.some((l) => l.sourceNodeId === sourceId && l.targetNodeId === targetId))
      return {valid: false, reason: 'duplicate'}

    const adjacency = new Map<string, string[]>()
    for (const link of existingLinks) {
      const targets = adjacency.get(link.sourceNodeId) ?? []
      targets.push(link.targetNodeId)
      adjacency.set(link.sourceNodeId, targets)
    }
    const newTargets = adjacency.get(sourceId) ?? []
    newTargets.push(targetId)
    adjacency.set(sourceId, newTargets)

    // Check for circular dependency: can we reach sourceId from targetId?
    const visited = new Set<string>()
    const hasCycle = (nodeId: string): boolean => {
      if (nodeId === sourceId) return true
      if (visited.has(nodeId) || isTerminal(nodeId)) return false
      visited.add(nodeId)
      for (const next of adjacency.get(nodeId) ?? []) {
        if (hasCycle(next)) return true
      }
      return false
    }
    if (!isTerminal(targetId) && hasCycle(targetId)) return {valid: false, reason: 'cycle'}

    // Check max chain depth: count entity nodes in longest path (terminals don't count)
    const chainLength = (nodeId: string, seen: Set<string>): number => {
      if (isTerminal(nodeId)) return 0
      if (seen.has(nodeId)) return 0
      seen.add(nodeId)
      let maxChild = 0
      for (const next of adjacency.get(nodeId) ?? []) {
        maxChild = Math.max(maxChild, chainLength(next, seen))
      }
      seen.delete(nodeId)
      return 1 + maxChild
    }

    // Find all roots (no incoming edges) and check depth
    const hasIncoming = new Set<string>()
    for (const [, targets] of adjacency) {
      for (const t of targets) {
        hasIncoming.add(t)
      }
    }
    for (const nodeId of adjacency.keys()) {
      if (!hasIncoming.has(nodeId)) {
        if (chainLength(nodeId, new Set()) > maxChainDepth)
          return {valid: false, reason: 'maxDepth'}
      }
    }

    return {valid: true}
  }

  return {isConnectionValid}
}
